import React, { useEffect, useRef } from 'react';
import Map from 'ol/Map.js';
import View from 'ol/View.js';
import { Vector as VectorLayer } from 'ol/layer.js';
import { Vector as VectorSource } from 'ol/source.js';
import Feature from 'ol/Feature.js';
import Point from 'ol/geom/Point.js';
import { Style, Circle, Fill, Stroke } from 'ol/style.js';
import { fromLonLat } from 'ol/proj.js';
import GeoJSON from 'ol/format/GeoJSON.js';
import Overlay from 'ol/Overlay.js';
import 'ol/ol.css';
import { getProjectStatistics } from '../../../../apis/impact-report/report';
import useFetchData from '../../../../hooks/useFetchData';
import { useSearchParams } from 'react-router-dom';
import Loader from '../../../../ui/Loader';

const LocationTab = ({ projectId,imageGenerationTimestamp }) => {
    const mapRef = useRef(null);
    const mapInstanceRef = useRef(null);
    
    // Fetch location data from API
    const { loading, response, error } = useFetchData(
        getProjectStatistics,
        [projectId, "BOUNTY_WISE_ACCEPTANCE_COORDINATES", imageGenerationTimestamp, ""],
        [projectId, "BOUNTY_WISE_ACCEPTANCE_COORDINATES", imageGenerationTimestamp, ""]
    );

    // Transform API response to location data format
    // First, extract all acceptances arrays and flatten them
    const allAcceptances = response?.bountyWiseAcceptanceCoordinates
        ?.flatMap(bounty => bounty?.acceptances || []) || [];
    
    // Transform each acceptance into the required format
    const coordsData = allAcceptances
        .map(acceptance => {
            const coords = acceptance?.acceptanceCoordinates;
            if (!coords || coords.length < 2) return null;
            
            return {
                lat: coords[0],
                lng: coords[1],
                timestamp: acceptance?.acceptanceTimestamp
            };
        })
        .filter(Boolean); // Remove null values
    
    // Use the transformed coordinates data
    const activeLocationData = coordsData;
    // Zoom functions
    const zoomIn = () => {
        if (mapInstanceRef.current) {
            const view = mapInstanceRef.current.getView();
            const currentZoom = view.getZoom();
            view.animate({ zoom: currentZoom + 1, duration: 300 });
        }
    };

    const zoomOut = () => {
        if (mapInstanceRef.current) {
            const view = mapInstanceRef.current.getView();
            const currentZoom = view.getZoom();
            view.animate({ zoom: currentZoom - 1, duration: 300 });
        }
    };

    useEffect(() => {
        if (!mapRef.current || loading) return;

        // Create vector source for markers
        const vectorSource = new VectorSource();

        // Add markers for each location
        activeLocationData.forEach(location => {
            const coordinate = fromLonLat([location.lng, location.lat]);
            
            const feature = new Feature({
                geometry: new Point(coordinate)
            });

            vectorSource.addFeature(feature);
        });

        // Create vector layer with custom styling
        const vectorLayer = new VectorLayer({
            source: vectorSource,
            style: new Style({
                image: new Circle({
                    radius: 3,
                    fill: new Fill({
                        color: '#0a6b5a' // Emerald green
                    }),
                    stroke: new Stroke({
                        color: '#0a6b5a', // Dark emerald border
                        width: 1
                    })
                })
            })
        });

        // Create landmass layer
        const landmassLayer = new VectorLayer({
            source: new VectorSource({
                url: 'https://raw.githubusercontent.com/holtzy/D3-graph-gallery/master/DATA/world.geojson',
                format: new GeoJSON()
            }),
            style: new Style({
                fill: new Fill({
                    color: '#9dd7b5' // Medium green landmasses
                }),
                stroke: new Stroke({
                    color: '#7bc4a1', // Darker green borders for definition
                    width: 1
                })
            })
        });

        // Initialize OpenLayers map
        const map = new Map({
            target: mapRef.current,
            layers: [landmassLayer, vectorLayer],
            view: new View({
                center: fromLonLat([0, 30]), // Center slightly higher for better vertical coverage
                zoom: 1.2 // Lower zoom for more world coverage
            }),
            controls: [] // Remove all default controls
        });

        // Store map instance
        mapInstanceRef.current = map;

        // Force map to resize properly
        setTimeout(() => {
            map.updateSize();
        }, 100);

        // Cleanup function
        return () => {
            if (mapInstanceRef.current) {
                mapInstanceRef.current.setTarget(null);
            }
        };
    }, [loading, response, activeLocationData]);

    if (loading) {
        return (
            <div className='w-full h-[23.9375rem] flex pt-11 justify-center'>
                <Loader />
            </div>
        );
    }


    return (
        <div className="space-y-6">
            <div className="w-full">
              
                {/* Map Container */}
                <div className="relative w-full h-[400px] bg-[#D6F5F0] rounded-lg overflow-hidden border border-gray-200">
                    <div 
                        ref={mapRef} 
                        className="w-full h-full"
                        style={{
                            background: '#D6F5F0' // Light green ocean background
                        }}
                    />
                    
                    {/* Zoom Controls */}
                    <div className="absolute top-4 right-4 flex flex-col gap-1">
                        {/* Zoom In Button */}
                        <button
                            onClick={zoomIn}
                            className="w-8 h-8 bg-grayBgNormal hover:bg-grayBgDark border border-borderUnderline rounded shadow-sm flex items-center justify-center transition-colors duration-200"
                            title="Zoom In"
                        >
                            <svg 
                                width="16" 
                                height="16" 
                                viewBox="0 0 24 24" 
                                fill="none" 
                                stroke="currentColor" 
                                strokeWidth="2" 
                                strokeLinecap="round" 
                                strokeLinejoin="round"
                                className="text-textPrimary"
                            >
                                <line x1="12" y1="5" x2="12" y2="19"></line>
                                <line x1="5" y1="12" x2="19" y2="12"></line>
                            </svg>
                        </button>
                        
                        {/* Zoom Out Button */}
                        <button
                            onClick={zoomOut}
                            className="w-8 h-8 bg-grayBgNormal hover:bg-grayBgDark border border-borderUnderline rounded shadow-sm flex items-center justify-center transition-colors duration-200"
                            title="Zoom Out"
                        >
                            <svg 
                                width="16" 
                                height="16" 
                                viewBox="0 0 24 24" 
                                fill="none" 
                                stroke="currentColor" 
                                strokeWidth="2" 
                                strokeLinecap="round" 
                                strokeLinejoin="round"
                                className="text-textPrimary"
                            >
                                <line x1="5" y1="12" x2="19" y2="12"></line>
                            </svg>
                        </button>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default LocationTab; 